import { useState } from 'react'
import { getFunctions, httpsCallable } from 'firebase/functions'
import { app } from '../lib/firebase'
import Modal from './Modal'
import Icon from './Icon'
import { COLUMNS, downloadTemplate, parseTourCsv } from '../lib/tourCsv'

/* ============================================================================
   Backfill.

   Scan, review, import. Never scan-and-import.

   The data is third party and artist names collide — there are four bands
   called Nirvana in the archive. Writing straight into someone's account on a
   name match is how you end up with a roster full of a Belgian covers band.
   So the scan returns a proposal, the user unticks what isn't theirs, and only
   then does anything get written.

   Re-running is safe: tours carry the source key they came from and shows
   carry the source id, so a second import adds what's new and skips the rest.
   ============================================================================ */

interface ProposedShow {
  sourceId: string
  date: string
  city: string
  country: string
  venueName: string
  venueLat: number | null
  venueLon: number | null
  /** Present on CSV rows, absent on scanned ones. */
  venueAddress?: string
  timezone?: string
  loadIn?: string
  soundCheck?: string
  doorsOpen?: string
  headlinerSetTime?: string
  packOut?: string
  contactName?: string
  contactEmail?: string
  contactPhone?: string
}

interface ProposedTour {
  key: string
  tourName: string
  startDate: string
  endDate: string
  shows: ProposedShow[]
  alreadyImported?: boolean
}

interface ScanResult {
  found: boolean
  query: string
  matchedName: string | null
  /** Which archive answered. Wikipedia needs no key and is the default. */
  source?: 'setlist.fm' | 'wikipedia' | 'csv'
  totalShows?: number
  tours: ProposedTour[]
}

const functions = getFunctions(app)
const scan = httpsCallable<{ artistId: string; query?: string }, ScanResult>(
  functions,
  'scanArtistTours',
)
const runImport = httpsCallable<
  { artistId: string; tours: ProposedTour[] },
  { toursCreated: number; showsCreated: number; showsSkipped: number }
>(functions, 'importArtistTours')

type Stage = 'idle' | 'scanning' | 'review' | 'importing' | 'done'
type Source = 'search' | 'csv'

export default function BackfillDialog({
  open,
  onClose,
  artistId,
  artistName,
}: {
  open: boolean
  onClose: () => void
  artistId: string
  artistName: string
}) {
  const [stage, setStage] = useState<Stage>('idle')
  const [query, setQuery] = useState(artistName)
  const [result, setResult] = useState<ScanResult | null>(null)
  const [chosen, setChosen] = useState<Set<string>>(new Set())
  const [error, setError] = useState<string | null>(null)
  const [summary, setSummary] = useState<string | null>(null)
  const [source, setSource] = useState<Source>('search')
  const [problems, setProblems] = useState<Array<{ line: number; message: string }>>([])

  /**
   * Reads a spreadsheet into the same proposal shape the scan produces, so the
   * review step, the tick boxes and the importer are all shared. A CSV is just
   * another way of proposing tours.
   */
  async function readFile(file: File) {
    setStage('scanning')
    setError(null)
    setProblems([])
    try {
      const parsed = parseTourCsv(await file.text())
      setProblems(parsed.problems)

      if (parsed.tours.length === 0) {
        setResult({ found: false, query: file.name, matchedName: null, source: 'csv', tours: [] })
        setStage('review')
        return
      }

      const tours: ProposedTour[] = parsed.tours.map((tour) => ({
        key: tour.key,
        tourName: tour.tourName,
        startDate: tour.startDate,
        endDate: tour.endDate,
        shows: tour.shows.map((show) => ({
          // Stable across re-imports of the same file, so running it twice
          // adds nothing rather than doubling the tour.
          sourceId: `csv:${tour.tourName}:${show.date}:${show.venueName}`,
          date: show.date,
          city: show.city,
          country: show.country,
          venueName: show.venueName,
          venueLat: null,
          venueLon: null,
          venueAddress: show.venueAddress,
          timezone: show.timezone,
          loadIn: show.loadIn,
          soundCheck: show.soundCheck,
          doorsOpen: show.doorsOpen,
          headlinerSetTime: show.headlinerSetTime,
          packOut: show.packOut,
          contactName: show.contactName,
          contactEmail: show.contactEmail,
          contactPhone: show.contactPhone,
        })),
      }))

      setResult({
        found: true,
        query: file.name,
        matchedName: file.name,
        source: 'csv',
        totalShows: tours.reduce((sum, tour) => sum + tour.shows.length, 0),
        tours,
      })
      setChosen(new Set(tours.map((tour) => tour.key)))
      setStage('review')
    } catch (caught) {
      setError(readable(caught))
      setStage('idle')
    }
  }

  async function doScan() {
    setStage('scanning')
    setError(null)
    try {
      const response = await scan({ artistId, query: query.trim() || artistName })
      setResult(response.data)
      // Everything not already in the account starts ticked. Opting out of a
      // wrong match is quicker than opting in to forty right ones.
      setChosen(
        new Set(
          response.data.tours.filter((tour) => !tour.alreadyImported).map((tour) => tour.key),
        ),
      )
      setStage('review')
    } catch (caught) {
      setError(readable(caught))
      setStage('idle')
    }
  }

  async function doImport() {
    if (!result) return
    const tours = result.tours.filter((tour) => chosen.has(tour.key))
    if (tours.length === 0) return

    setStage('importing')
    setError(null)
    try {
      const response = await runImport({ artistId, tours })
      const { toursCreated, showsCreated, showsSkipped } = response.data
      setSummary(
        `${toursCreated} ${toursCreated === 1 ? 'tour' : 'tours'} and ${showsCreated} ` +
          `${showsCreated === 1 ? 'show' : 'shows'} added` +
          (showsSkipped > 0 ? `, ${showsSkipped} already there` : '') +
          '.',
      )
      setStage('done')
    } catch (caught) {
      setError(readable(caught))
      setStage('review')
    }
  }

  function toggle(key: string) {
    setChosen((current) => {
      const next = new Set(current)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }

  function reset() {
    setStage('idle')
    setProblems([])
    setResult(null)
    setChosen(new Set())
    setError(null)
    setSummary(null)
    onClose()
  }

  const selectable = (result?.tours ?? []).filter((tour) => !tour.alreadyImported)
  const showCount = (result?.tours ?? [])
    .filter((tour) => chosen.has(tour.key))
    .reduce((sum, tour) => sum + tour.shows.length, 0)

  return (
    <Modal
      open={open}
      title="Backfill tours"
      onClose={reset}
      width={640}
      footer={
        stage === 'review' ? (
          <div className="spread" style={{ width: '100%', alignItems: 'center' }}>
            <span className="t-caption">
              {chosen.size} of {selectable.length} selected · {showCount} shows
            </span>
            <div className="row" style={{ gap: 8 }}>
              <button className="btn btn-quiet" onClick={reset}>
                Cancel
              </button>
              <button
                className="btn btn-primary"
                disabled={chosen.size === 0}
                onClick={doImport}
              >
                Import {chosen.size > 0 ? chosen.size : ''}
              </button>
            </div>
          </div>
        ) : undefined
      }
    >
      <div className="stack" style={{ gap: 16 }}>
        {/* ------------------------------------------------------- idle */}
        {stage === 'idle' && (
          <>
            {/* Two ways in, because half the people who need this already have
                the dates in a spreadsheet and half are starting from a name. */}
            <div className="row" style={{ gap: 4, padding: 3, borderRadius: 999, background: 'var(--panel-raised)', alignSelf: 'flex-start' }}>
              {(['search', 'csv'] as Source[]).map((option) => {
                const on = option === source
                return (
                  <button
                    key={option}
                    type="button"
                    className="chip"
                    aria-pressed={on}
                    onClick={() => setSource(option)}
                    style={{
                      cursor: 'pointer',
                      border: 0,
                      background: on ? 'var(--panel)' : 'transparent',
                      color: on ? 'var(--accent-text)' : 'var(--text-secondary)',
                      boxShadow: on ? 'var(--shadow-card)' : undefined,
                    }}
                  >
                    {option === 'search' ? 'Search the archive' : 'Upload a spreadsheet'}
                  </button>
                )
              })}
            </div>

            {source === 'search' ? (
              <>
                <p className="t-body dim">
                  Looks up this artist&rsquo;s concert history and turns it into tours and shows you
                  can edit. Dates and venues come from Wikipedia. Nothing is written until
                  you&rsquo;ve seen the list.
                </p>
                <div className="stack" style={{ gap: 6 }}>
                  <span className="t-eyebrow">Search as</span>
                  <input
                    className="input"
                    value={query}
                    onChange={(event) => setQuery(event.target.value)}
                    placeholder={artistName}
                  />
                  <span className="t-caption">
                    Change this if the act records under a different name.
                  </span>
                </div>
                {error && <p className="t-caption error">{error}</p>}
                <button className="btn btn-primary" onClick={doScan}>
                  <Icon name="search" size={15} /> Scan
                </button>
              </>
            ) : (
              <>
                <p className="t-body dim">
                  One row per show. Rows sharing a tour name become one tour, and the run&rsquo;s
                  dates come from its first and last show. You&rsquo;ll see everything before
                  anything is written.
                </p>

                <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
                  <label className="btn btn-primary" style={{ cursor: 'pointer' }}>
                    <Icon name="plus" size={15} /> Choose a CSV
                    <input
                      type="file"
                      accept=".csv,text/csv"
                      style={{ display: 'none' }}
                      onChange={(event) => {
                        const file = event.target.files?.[0]
                        if (file) void readFile(file)
                        event.target.value = ''
                      }}
                    />
                  </label>
                  <button className="btn btn-secondary" onClick={downloadTemplate}>
                    <Icon name="export" size={15} /> Download the template
                  </button>
                </div>

                {error && <p className="t-caption error">{error}</p>}

                <details className="panel panel-pad">
                  <summary style={{ cursor: 'pointer', fontWeight: 600 }}>
                    What the columns mean
                  </summary>
                  <div className="stack" style={{ gap: 7, marginTop: 12 }}>
                    {COLUMNS.map((column) => (
                      <div key={column.key} className="row" style={{ gap: 10, alignItems: 'baseline' }}>
                        <code
                          className="t-spec-caption"
                          style={{ minWidth: 116, color: column.required ? 'var(--accent-text)' : undefined }}
                        >
                          {column.key}
                          {column.required ? ' *' : ''}
                        </code>
                        <span className="t-caption grow">
                          {column.note || column.example || '—'}
                        </span>
                      </div>
                    ))}
                    <p className="t-caption" style={{ marginTop: 4 }}>
                      Starred columns are required. Anything else can be blank, and columns Encore
                      doesn&rsquo;t recognise are ignored rather than rejected.
                    </p>
                  </div>
                </details>
              </>
            )}
          </>
        )}

        {stage === 'scanning' && (
          <p className="t-body dim">
            {source === 'csv' ? 'Reading the spreadsheet…' : 'Searching the archive…'}
          </p>
        )}
        {stage === 'importing' && <p className="t-body dim">Writing tours and shows…</p>}

        {/* ----------------------------------------------------- review */}
        {stage === 'review' && result && (
          <>
            {!result.found || result.tours.length === 0 ? (
              <div className="stack" style={{ gap: 10 }}>
                <p className="t-body dim">
                  {result.source === 'csv'
                    ? `Nothing readable in ${result.query}.`
                    : `Nothing found for ${result.query}. Try the name exactly as it appears on a ticket.`}
                </p>
                <button className="btn btn-secondary" onClick={() => setStage('idle')}>
                  Search again
                </button>
              </div>
            ) : (
              <>
                <div className="panel panel-pad stack" style={{ gap: 4 }}>
                  <span style={{ fontWeight: 600 }}>Matched {result.matchedName}</span>
                  <span className="t-caption">
                    {result.totalShows} shows, grouped into {result.tours.length} tours, from{' '}
                    {result.source === 'csv'
                      ? 'your spreadsheet'
                      : result.source === 'setlist.fm'
                        ? 'setlist.fm'
                        : 'Wikipedia'}
                    . Untick anything you don&rsquo;t want.
                  </span>
                </div>

                {error && <p className="t-caption error">{error}</p>}

                {problems.length > 0 && (
                  <details className="panel panel-pad">
                    <summary style={{ cursor: 'pointer', color: 'var(--warning)' }}>
                      {problems.length} row{problems.length === 1 ? '' : 's'} skipped
                    </summary>
                    <div className="stack" style={{ gap: 4, marginTop: 10 }}>
                      {problems.slice(0, 40).map((problem) => (
                        <span key={`${problem.line}-${problem.message}`} className="t-caption">
                          Line {problem.line}: {problem.message}
                        </span>
                      ))}
                    </div>
                  </details>
                )}

                <div className="stack" style={{ gap: 6, maxHeight: 380, overflowY: 'auto' }}>
                  {result.tours.map((tour) => {
                    const done = tour.alreadyImported
                    return (
                      <label
                        key={tour.key}
                        className="row panel"
                        style={{
                          padding: '11px 13px',
                          gap: 11,
                          alignItems: 'flex-start',
                          cursor: done ? 'default' : 'pointer',
                          opacity: done ? 0.55 : 1,
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={chosen.has(tour.key)}
                          disabled={done}
                          onChange={() => toggle(tour.key)}
                          style={{ marginTop: 3 }}
                        />
                        <span className="grow stack" style={{ gap: 2, minWidth: 0 }}>
                          <span className="truncate" style={{ fontWeight: 600 }}>
                            {tour.tourName}
                          </span>
                          <span className="t-caption">
                            {tour.startDate} to {tour.endDate} · {tour.shows.length} shows
                          </span>
                          <span className="t-caption truncate">
                            {[...new Set(tour.shows.map((show) => show.city))]
                              .slice(0, 5)
                              .join(', ')}
                          </span>
                        </span>
                        {done && <span className="chip">Already in</span>}
                      </label>
                    )
                  })}
                </div>
              </>
            )}
          </>
        )}

        {/* ------------------------------------------------------- done */}
        {stage === 'done' && (
          <div className="stack" style={{ gap: 12 }}>
            <p className="t-body">{summary}</p>
            <p className="t-caption">
              They&rsquo;re real tours now: rename them, fix a venue, add travel. Public archives
              miss the odd date and get the odd venue wrong, so give them a read. Running this
              again will only add what&rsquo;s missing.
            </p>
            <button className="btn btn-primary" onClick={reset}>
              Done
            </button>
          </div>
        )}
      </div>
    </Modal>
  )
}

/** Callable errors arrive as `{ message }`; anything else is a network fault. */
function readable(caught: unknown): string {
  if (caught && typeof caught === 'object' && 'message' in caught) {
    const message = String((caught as { message: unknown }).message)
    if (message && !message.startsWith('INTERNAL')) return message
  }
  return 'That did not work. Try again in a moment.'
}
